/**
 * Reports Page
 * Generate daily and weekly summary reports
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  FileText,
  Calendar,
  Download,
  TrendingUp,
  Users,
  Video,
  Gamepad2,
  RefreshCw,
  ChevronRight
} from 'lucide-react';

const ReportsPage = () => {
  const { api } = useAdmin();
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReports();
  }, [selectedDate]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [daily, weekly] = await Promise.all([
        api.get(`/api/admin/reports/daily?date=${selectedDate}`),
        api.get('/api/admin/reports/weekly')
      ]);
      setDailyReport(daily.data);
      setWeeklyReport(weekly.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = (report, type) => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${type}_report_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Report exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Summary Reports</h1>
          <p className="text-gray-500 text-sm mt-1">Generate daily and weekly reports</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={fetchReports}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('daily')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'daily' ? 'text-emerald-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" />
              Daily Report
            </div>
            {activeTab === 'daily' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('weekly')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'weekly' ? 'text-emerald-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Weekly Report
            </div>
            {activeTab === 'weekly' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
            )}
          </button>
        </div>

        {/* Daily Report */}
        {activeTab === 'daily' && dailyReport && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Daily Summary</h2>
                <p className="text-sm text-gray-500">
                  {new Date(dailyReport.report_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <button
                onClick={() => exportReport(dailyReport, 'daily')}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* User Metrics */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-emerald-900">User Metrics</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-emerald-700">New Signups</span>
                    <span className="font-bold text-emerald-900">
                      {dailyReport.user_metrics?.new_signups || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-700">Active Users</span>
                    <span className="font-bold text-emerald-900">
                      {dailyReport.user_metrics?.active_users || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stream Metrics */}
              <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Video className="w-5 h-5 text-rose-600" />
                  <h3 className="font-semibold text-rose-900">Stream Metrics</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-rose-700">Total Streams</span>
                    <span className="font-bold text-rose-900">
                      {dailyReport.stream_metrics?.total_streams || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-rose-700">Total Viewers</span>
                    <span className="font-bold text-rose-900">
                      {dailyReport.stream_metrics?.total_viewers || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Game Metrics */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Gamepad2 className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-900">Game Metrics</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-amber-700">Total Games</span>
                    <span className="font-bold text-amber-900">
                      {dailyReport.game_metrics?.total_games || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Completed</span>
                    <span className="font-bold text-amber-900">
                      {dailyReport.game_metrics?.completed_games || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Weekly Report */}
        {activeTab === 'weekly' && weeklyReport && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Weekly Summary</h2>
                <p className="text-sm text-gray-500">
                  {new Date(weeklyReport.week_start).toLocaleDateString()} - {new Date(weeklyReport.week_end).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => exportReport(weeklyReport, 'weekly')}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>

            {/* Weekly Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {weeklyReport.summary?.total_new_users || 0}
                </p>
                <p className="text-sm text-gray-500">New Users</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {weeklyReport.summary?.avg_daily_active_users || 0}
                </p>
                <p className="text-sm text-gray-500">Avg. DAU</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {weeklyReport.summary?.total_streams || 0}
                </p>
                <p className="text-sm text-gray-500">Total Streams</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {weeklyReport.summary?.total_games || 0}
                </p>
                <p className="text-sm text-gray-500">Total Games</p>
              </div>
            </div>

            {/* Daily Breakdown */}
            <h3 className="font-semibold text-gray-900 mb-4">Daily Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">New Users</th>
                    <th className="py-3 px-4">Active Users</th>
                    <th className="py-3 px-4">Streams</th>
                    <th className="py-3 px-4">Games</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {weeklyReport.daily_breakdown?.map((day, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">
                        {new Date(day.report_date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {day.user_metrics?.new_signups || 0}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {day.user_metrics?.active_users || 0}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {day.stream_metrics?.total_streams || 0}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {day.game_metrics?.total_games || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
